import React, { useState, useEffect, Fragment } from 'react';
import { useSearchParams } from 'react-router-dom';
import { submissions } from '../utils/api';
import SubmissionModal from '../components/SubmissionModal';
import { emitEvent, EVENTS } from '../utils/events';
import { TableProvider } from '../components/shared/table/context/TableContext';
import { SubmissionListTable } from '../features/submissions/SubmissionListTable';

export default function Submissions() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('type') || 'active');
  const [pendingTab, setPendingTab] = useState(null);
  const [submissionsList, setSubmissionsList] = useState([]);
  const [loadingStates, setLoadingStates] = useState({
    active: true,
    history: true
  });
  const [error, setError] = useState(null);
  const [checkingStatus, setCheckingStatus] = useState({});
  const [checkingAllStatus, setCheckingAllStatus] = useState(false);
  const [editingSubmission, setEditingSubmission] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const handleTabChange = (tab) => {
    if (tab === pendingTab) return;
    setPendingTab(tab);
    
    loadSubmissions().then(() => {
      setActiveTab(tab);
      setPendingTab(null);
      setSearchParams(params => {
        params.set('type', tab);
        return params;
      });
    });
  };

  useEffect(() => {
    loadSubmissions();
  }, []);

  const loadSubmissions = async () => {
    try {
      setLoadingStates(prev => ({
        active: true,
        history: true
      }));
      const data = await submissions.getAll();
      setSubmissionsList(data);
    } catch (err) {
      setError('Failed to load submissions');
    } finally {
      setLoadingStates(prev => ({
        active: false,
        history: false
      }));
    }
  };

  const handleCheckPCGSStatus = async (submissionId) => {
    setCheckingStatus(prev => ({ ...prev, [submissionId]: true }));
    try {
      await submissions.checkPCGSStatus(submissionId);
      await loadSubmissions();
      emitEvent(EVENTS.NOTIFICATION_UPDATE);
    } catch (err) {
      console.error('PCGS Status Check Error:', err);
      setError('Failed to check PCGS status');
    } finally {
      setCheckingStatus(prev => ({ ...prev, [submissionId]: false }));
    }
  };

  const handleCheckAllPCGSStatus = async () => {
    setCheckingAllStatus(true);
    try {
      const pcgsSubmissions = activeSubmissions.filter(s => s.grading_service_name === 'PCGS');
      
      // Set all submissions to checking state
      setCheckingStatus(prev => {
        const newState = { ...prev };
        pcgsSubmissions.forEach(submission => {
          newState[submission.id] = true;
        });
        return newState;
      });

      // Check all submissions in parallel
      await Promise.all(
        pcgsSubmissions.map(async (submission) => {
          try {
            await submissions.checkPCGSStatus(submission.id);
            emitEvent(EVENTS.NOTIFICATION_UPDATE);
          } catch (err) {
            console.error(`Failed to check status for submission ${submission.id}:`, err);
          }
        })
      );

      // Refresh the list after all updates
      await loadSubmissions();
    } catch (err) {
      console.error('Check All PCGS Status Error:', err);
      setError('Failed to check PCGS status for all submissions');
    } finally {
      // Reset all checking states
      setCheckingStatus({});
      setCheckingAllStatus(false);
    }
  };

  const handleCloseEdit = () => {
    setEditingSubmission(null);
    loadSubmissions(); // Refresh the list after editing
  };

  const { activeSubmissions, historySubmissions } = React.useMemo(() => {
    const active = [];
    const history = [];
    
    submissionsList.forEach(submission => {
      if (submission.in_history) {
        history.push(submission);
      } else {
        active.push(submission);
      }
    });

    return { activeSubmissions: active, historySubmissions: history };
  }, [submissionsList]);

  return (
    <div className="container mx-auto p-4 h-full flex flex-col">
      <div className="mb-6">
        <h1 className="text-2xl font-bold">Submissions</h1>
        <div className="mt-4">
          <div className="flex justify-between items-center">
            <nav className="-mb-px flex space-x-8 border-b border-gray-200" aria-label="Tabs">
            <button
              onClick={() => handleTabChange('active')}
              disabled={pendingTab === 'active'}
              className={`
                ${activeTab === 'active'
                  ? 'border-indigo-500 text-indigo-600'
                  : pendingTab === 'active'
                  ? 'border-transparent text-gray-400 cursor-wait'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }
                whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm relative
              `}
            >
              Active
              {pendingTab === 'active' && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-4 h-4 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
                </div>
              )}
            </button>
            <button
              onClick={() => handleTabChange('history')}
              disabled={pendingTab === 'history'}
              className={`
                ${activeTab === 'history'
                  ? 'border-indigo-500 text-indigo-600'
                  : pendingTab === 'history'
                  ? 'border-transparent text-gray-400 cursor-wait'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }
                whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm relative
              `}
            >
              History
              {pendingTab === 'history' && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-4 h-4 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
                </div>
              )}
            </button>
            </nav>
            <div className="flex items-center gap-2">
              {activeTab === 'active' && (
                <button
                  type="button"
                  onClick={handleCheckAllPCGSStatus}
                  disabled={checkingAllStatus}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                >
                  {checkingAllStatus ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                      Checking...
                    </>
                  ) : (
                    'Update All'
                  )}
                </button>
              )}
              <button
                onClick={() => setShowCreateModal(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                New Submission
              </button>
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="rounded-md bg-red-50 p-4 my-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-red-800">{error}</p>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow flex-1 min-h-0">
        <TableProvider 
          type={`submission_${activeTab}`}
          initialState={{ 
            loading: loadingStates[activeTab], 
            error,
            preferences: {
              density: 'standard',
              columnWidths: {
                actions: 50,
                grading_service_name: 100,
                coins: 100,
                submission_number: 150,
                order_number: 120,
                date_received: 130,
                service_type: 130,
                status: 140,
                date_shipped: 130,
                grade_ready: 120,
                image_ready: 120,
                last_checked_at: 160,
                tracking_number: 140
              },
              hiddenColumns: [],
              filters: [],
              sortConfig: [],
              quickFilter: '',
              pinnedColumns: [],
              columnOrder: []
            }
          }}
        >
          <SubmissionListTable
            submissions={activeTab === 'active' ? activeSubmissions : historySubmissions}
            type={activeTab}
            onCheckStatus={handleCheckPCGSStatus}
            checkingStatus={checkingStatus}
          />
        </TableProvider>
      </div>

      {/* Edit Modal */}
      {editingSubmission && (
        <SubmissionModal
          initialData={editingSubmission}
          onClose={handleCloseEdit}
        />
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <SubmissionModal
          onClose={() => {
            setShowCreateModal(false);
            loadSubmissions();
          }}
        />
      )}

    </div>
  );
}
